import React, { useEffect, useState } from "react";

/**
 * Reusable, vertically-scrollable modal with Close + Action buttons.
 *
 * Props:
 *  - buttonText (string): label for the trigger button.
 *  - actionText (string): label for the action button inside the modal.
 *  - onAction (function): callback invoked when the action button is clicked.
 *  - setVisibility (function, optional): called with true/false when modal opens/closes.
 *  - closeOnAction (boolean, optional): close modal after onAction runs. Default: true.
 *  - children (ReactNode): modal body content.
 */
function ModalWithAction({
  buttonText = "Open Modal",
  actionText = "Yes",
  closeButtonText="Close",
  onAction = () => {},
  children,
}) {
  const [open, setOpen] = useState(false);

  const openModal = () => {
    setOpen(true);
    //if (setVisibility) setVisibility(true);
  };

  const closeModal = () => {
    setOpen(false);
   // if (setVisibility) setVisibility(false);
  };

  const handleAction = () => {
    try {
      onAction();
    } finally {
       closeModal();
    }
  };

  // Close on ESC key
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={openModal}
        className=" rounded-full  px-4 py-2 font-bold
         text-teal-700 text-center bg-white hover:bg-teal-100 
          focus:ring-2 focus:ring-teal-500 focus:outline-none
          focus:ring-offset-2 active:scale-95 transition"
      
      >
        {buttonText}
      </button>

      {/* Backdrop & Modal */}
      {open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          {/* Click outside to close */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeModal}
          />
          {/* Dialog card */}
          <div
            role="dialog"
            aria-modal="true"
            className="relative z-50 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
          >
            <div className="mb-6">{children}</div>

            <div className="flex justify-end gap-3">
              {/* Close button */}
              <button
                onClick={closeModal}
                className="rounded-md border border-teal-200 px-4 py-2
                 font-medium text-teal-700 hover:bg-teal-50 
                 focus:outline-none focus:ring-2 focus:ring-teal-400"
              >
                {closeButtonText}
              </button>
              {/* Action button with dynamic title */}
              <button
                onClick={handleAction}
                className="rounded-md bg-teal-600 px-4 py-2 
                font-semibold text-white shadow hover:bg-teal-700 
                focus:outline-none focus:ring-2 focus:ring-teal-400"
              >
                {actionText}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export { ModalWithAction };

/* -------------------------
   Usage example:

<ModalWithAction
  buttonText="Open Section Modal"
  actionText="Move to Next Section"
  onAction={moveToNextSection}
>
  <h2 className="text-xl font-semibold">Section Complete</h2>
  <p className="mt-2 text-gray-600">
    Review your answers or move to the next section.
  </p>
</ModalWithAction>

------------------------- */
